import React, { useCallback } from "react";
import { EuiToolTip } from "@elastic/eui";
import { useAnketaAuth } from "src/auth/context";
import {
  AnketaCheckPermissionOptions,
  AnketaResources,
} from "src/auth/types";
import { opsanioCan } from "src/auth/functions/opsanio-can";
import { useAnketaI18n } from "src/internationalization";

export type OpsanioCan = (
  resource: AnketaResources,
  action: string,
  options?: AnketaCheckPermissionOptions<AnketaResources>,
) => boolean;

/**
 * `can(resource, action, options?)` for opsanio action affordances: superuser,
 * or internal org AND the live matrix grant - the same rule the server applies.
 * Combine requirements with `&&` where a route checks several resources.
 */
export const useOpsanioCan = (): OpsanioCan => {
  const { user, permissionMatrix } = useAnketaAuth();
  return useCallback<OpsanioCan>(
    (resource, action, options) =>
      opsanioCan(user, resource, action, options, permissionMatrix),
    [user, permissionMatrix],
  );
};

/**
 * Gates one action control. `hide` (default) drops it; `disable` keeps it in
 * place, disabled, with a "no permission" tooltip - for primary workflow
 * buttons whose absence would be confusing.
 */
export const OpsanioActionGate = ({
  allowed,
  mode = "hide",
  children,
}: {
  allowed: boolean;
  mode?: "hide" | "disable";
  children: React.ReactElement;
}): JSX.Element | null => {
  const { translate } = useAnketaI18n();
  if (allowed) return children;
  if (mode === "hide") return null;
  return (
    <EuiToolTip
      content={translate("anketa.permission.denied.action", {
        defaultMessage: "You do not have permission for this action",
      })}
    >
      {React.cloneElement(children, { isDisabled: true, disabled: true })}
    </EuiToolTip>
  );
};
